import * as React from "react";
import { Badge } from "#app/components/ui/badge.tsx";
import { Icon, type IconName } from "#app/components/ui/icon.tsx";
import { getConnectorInfo, getNetworkName } from "#app/utils/connectors.ts";

// Define internal types
interface Outlet {
  id: number;
  connectorName: string | null;
  connector: string | null;
  kilowatts: number | null;
}

interface StationItem {
  id: number;
  networkId: number | null;
  outlets: Array<Outlet>;
}

interface ChargerType {
  networkId: string | null;
  connector: string;
  count: number;
}

// Update props
interface ConnectorListProps {
  stations: Array<StationItem> | undefined;
}

export function ConnectorList({ stations }: ConnectorListProps) {
  // Move calculation logic here
  let chargerTypes: ChargerType[] = [];
  if (stations && stations.length > 0) {
    stations.forEach((stationItem) => {
      const { networkId } = stationItem;
      const networkIdStr = networkId !== null ? networkId.toString() : null;
      stationItem.outlets.forEach((outlet) => {
        const { connector } = outlet;
        if (connector != null) {
          const connectorStr = connector.toString();
          let entry = chargerTypes.find(
            (e) => e.networkId === networkIdStr && e.connector === connectorStr,
          );
          if (entry) {
            entry.count += 1;
          } else {
            chargerTypes.push({
              networkId: networkIdStr,
              connector: connectorStr,
              count: 1,
            });
          }
        }
      });
    });
  }

  const totalNumberOfChargers = chargerTypes.reduce(
    (sum, { count }) => sum + count,
    0,
  );

  // Original return logic
  if (totalNumberOfChargers <= 0) {
    return null;
  }

  return (
    <div className="w-full rounded-lg border border-border bg-white p-4 shadow-md dark:bg-gray-800">
      <h2 className="mb-4 text-xl font-semibold text-card-foreground">
        Conectores ({totalNumberOfChargers})
      </h2>
      <ul className="space-y-4">
        {chargerTypes.map((chargerType) => {
          // @ts-ignore - Keep ignore for now
          const connectorInfo = getConnectorInfo(chargerType.connector);
          const networkName = chargerType.networkId
            ? getNetworkName(chargerType.networkId)
            : null;
          const iconName = connectorInfo?.icon || "plug";

          return (
            <li
              key={`${chargerType.networkId}-${chargerType.connector}`}
              className="flex items-center justify-between rounded-lg bg-[#f9fafc] p-4 dark:bg-gray-700"
            >
              <div className="flex items-center space-x-4 font-semibold">
                {connectorInfo && (
                  <Icon
                    className="text-[60px] md:text-[80px]"
                    // Use the determined icon name. Add @ts-ignore if type error persists
                    // @ts-ignore
                    name={iconName}
                  />
                )}
                <div>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {connectorInfo ? connectorInfo.name : chargerType.connector}
                  </p>
                  {networkName && (
                    <p className="text-sm font-semibold text-muted-foreground">
                      {networkName}
                    </p>
                  )}
                </div>
              </div>
              <Badge
                variant="outline"
                className="flex min-w-[100px] items-center justify-center dark:border-gray-600 dark:text-gray-300"
              >
                {chargerType.count}{" "}
                {chargerType.count > 1 ? "conectores" : "conector"}
              </Badge>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
